import { zodResolver } from '@hookform/resolvers/zod';
import {
  useForm,
  FieldValues,
  FormState,
  UseFormReturn,
  UseFormProps,
  Path,
  PathValue,
  FieldPathValue,
  DefaultValues,
  RegisterOptions,
  Resolver,
} from 'react-hook-form';
import { getZodDefaults } from '@repo/util/getZodDefaults';
import { TypeOf, z } from 'zod';
import { ZodObjecty, TypeOfOptional } from '@repo/types/Types';
import { useMemo } from 'react';
import { isValid, format } from 'date-fns';

export interface UseZodFormProps<Schema extends ZodObjecty | undefined>
  extends UseFormProps<TypeOfOptional<Schema>> {
  onSubmit?: (data: TypeOfOptional<Schema>) => unknown | Promise<unknown>;
  schema?: Schema;
}

export function registerInput<TFormValues extends FieldValues, TPath extends Path<TFormValues>>(
  form: Pick<UseFormReturn<TFormValues>, 'register'>,
  path: TPath,
  options?: RegisterOptions<TFormValues, TPath>
) {
  return form.register(path, options);
}

export function registerToggle<TFormValues extends FieldValues, TPath extends Path<TFormValues>>(
  form: UseFormReturn<TFormValues>,
  path: TPath
) {
  const value = form.watch(path);
  return {
    name: path,
    checked: typeof value === 'boolean' ? value : /^true$/gi.test(value),
    value: 'true',
    onChange(checked: boolean) {
      //TODO: orignially  !!checked as PathValue<TFormValues, TPath>, but caused error after react hook form upgrade
      form.setValue(path, !!checked as any, {
        shouldDirty: true,
      });
    },
  };
}

export function registerDate<
  TPath extends string,
  TForm extends {
    watch(path: TPath): unknown;
    setValue(
      path: TPath,
      value: unknown,
      opts: {
        shouldDirty: true;
        shouldTouch: true;
      }
    ): unknown;
  },
>(form: TForm, path: TPath) {
  const value = form.watch(path);

  return {
    name: path,
    selected: value as Date | null,
    onChange: (date: Date | null): void => {
      const formattedDate = date
        ? isValid(date)
          ? format(new Date(date), 'yyyy-MM-dd')
          : null
        : null;

      form.setValue(path, formattedDate, {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
  };
}

export function registerDateRange<
  TPath extends string,
  TForm extends {
    watch(path: TPath): unknown;
    setValue(
      path: TPath,
      value: unknown,
      opts: {
        shouldDirty: true;
        shouldTouch: true;
      }
    ): unknown;
  },
>(form: TForm, start: TPath, end: TPath) {
  return {
    start: registerDate(form, start),
    end: registerDate(form, end),
  };
}

export function registerAccountSelector<
  TPath extends string,
  TForm extends {
    watch(path: TPath): unknown;
    setValue(path: TPath, value: unknown, opts: { shouldDirty: true; shouldTouch: true }): unknown;
  },
>(form: TForm, path: TPath) {
  const value = form.watch(path);
  return {
    name: path,
    value: value ? { label: null, value: value as string } : undefined,
    onChange: (selected: { label: string; value: string } | null) => {
      form.setValue(path, selected?.value ?? '', {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
  };
}

export function isZodFormReturn<Schema extends ZodObjecty | undefined>(
  formReturn: UseFormReturn | UseZodFormReturn<Schema>
): formReturn is UseZodFormReturn<Schema> {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  return typeof formReturn.formState?.onSubmit === 'function';
}

export function isActiveZodFormReturn<Schema extends ZodObjecty | undefined>(
  formReturn: UseFormReturn | UseZodFormReturn<Schema>
): formReturn is Schema extends ZodObjecty ? UseZodFormReturn<Schema> : never {
  return isZodFormReturn(formReturn) && !!formReturn.formState;
}

export type ZodFormState<Schema extends ZodObjecty = ZodObjecty> = FormState<TypeOf<Schema>> & {
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
};

type ZodFormExtras<Schema extends ZodObjecty | undefined> = {
  formState: ZodFormState<Schema extends ZodObjecty ? Schema : SimpleObjectSchema>;
  schema: Schema extends ZodObjecty ? Schema : SimpleObjectSchema;
};

export type UseZodFormReturn<Schema extends ZodObjecty | undefined = ZodObjecty | undefined> = Omit<
  UseFormReturn<TypeOfOptional<Schema>>,
  'formState'
> &
  ZodFormExtras<Schema>;

export const SIMPLE_OBJECT_SCHEMA = z.object({});
export type SimpleObjectSchema = typeof SIMPLE_OBJECT_SCHEMA;

export function useZodForm<Schema extends ZodObjecty | undefined = ZodObjecty | undefined>({
  schema,
  onSubmit,
  ...options
}: UseZodFormProps<Schema>): UseZodFormReturn<Schema> {
  const resolver = zodResolver(schema ?? SIMPLE_OBJECT_SCHEMA) as unknown as Resolver<
    TypeOfOptional<Schema>
  >;
  const form = useForm<TypeOfOptional<Schema>>({
    ...options,
    resolver,
    defaultValues:
      options.defaultValues ??
      (schema ? (getZodDefaults(schema) as DefaultValues<TypeOfOptional<Schema>>) : undefined),
  });

  const extras = useMemo((): ZodFormExtras<Schema> => {
    const formState: ZodFormState<NonNullable<Schema>> = {
      ...form.formState,
      onSubmit: form.handleSubmit(async (data) => {
        try {
          await onSubmit?.(data);
        } catch (err) {
          form.control._subjects.state.next({
            isSubmitSuccessful: false,
          });
          form.control.setError('root.server', {
            type: 'response-error',
            message:
              err instanceof Error ? err.message : typeof err === 'string' ? err : 'unknown-error',
          });
        }
      }),
    };

    return { schema, formState } as ZodFormExtras<Schema>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.formState, form.handleSubmit, form.control, onSubmit, schema]);
  return useMemo((): UseZodFormReturn<Schema> => {
    return {
      ...form,
      ...extras,
    };
  }, [form]);
}
